import React, { useEffect, useState, useRef } from 'react';
import axiosClient from '../../api/axiosClient';
import { Globe, Shield, Cpu, Activity, Database, Terminal, Server, Wifi, Brain } from 'lucide-react';

const ICONS = {
  'NETWALL': Globe, 'SENTINEL': Shield, 'KERNEL_BPF': Cpu,
  'WATCHDOG': Activity, 'AI_CORE': Brain, 'HIVE_MIND': Database,
  'FORENSICS': Server, 'NET_GUARD': Wifi
};


const MatrixFeed = () => {
  const [displayLogs, setDisplayLogs] = useState([]); 
  const [stats, setStats] = useState({ total_analyzed: 0, threats_blocked: 0 });
  const logQueue = useRef([]); 

  // 1. Data Fetcher (Fills the Queue)
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const newLogs = await axiosClient.get('/public/matrix-feed');
        
        logQueue.current.push(...newLogs);
        const newStats = await axiosClient.get('/public/stats');
        setStats(newStats);

      } catch (error) { console.error("Matrix error"); }
    };

    fetchLogs();
    const apiInterval = setInterval(fetchLogs, 5000); 
    return () => clearInterval(apiInterval);
  }, []);

  // 2. The Printer (Runs fast, smooths the flow)
  useEffect(() => {
    const printer = setInterval(() => {
      if (logQueue.current.length > 0) {
        const nextLog = logQueue.current.shift(); 
        
        // Add to display (Top to Bottom flow)
        setDisplayLogs(prev => {
          const updated = [nextLog, ...prev];
          return updated.slice(0, 12); 
        });
      }
    }, 800); 

    return () => clearInterval(printer);
  }, []);

  const formatLargeNumber = (num) => {
  if (!num) return "0";

  if (num >= 1_000_000_000_000) {
    return (num / 1_000_000_000_000).toFixed(2) + "T+";
  }

  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + "B+";
  }

  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + "M+";
  }

  if (num >= 1_000) {
    return (num / 1_000).toFixed(2) + "K+";
  }

  return num.toString();
};

  return (
    <div className="w-full bg-[#020617] border-y border-slate-800/50 font-mono text-xs relative h-[400px] overflow-hidden flex flex-col">
      
      {/* Header Bar */}
      
      <div className="bg-[#0f172a] px-6 py-2 flex justify-between items-center z-20 border-b border-slate-800">
         <span className="text-emerald-500 font-bold tracking-widest flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            LIVE NEURAL UPLINK
         </span>
         <span className="text-slate-500">PORT: 443 [SECURE]</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-t border-slate-800/50 bg-[#020617] relative z-20">
         
         {/* Metric 1 */}
      <div className="space-y-1">
          <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Events Analyzed</p>
          <p className="text-emerald-400 font-mono text-[clamp(0.95rem,1.4vw,1.125rem)] animate-pulse break-words leading-tight">
            {formatLargeNumber(stats.total_analyzed)}
          </p>
          <p className="text-[9px] text-slate-600 leading-tight">Continuously processed system activities</p>
      </div>

      {/* Metric 2 */}
      <div className="space-y-1">
          <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Neutralized</p>
          <p className="text-red-400 font-mono text-[clamp(0.95rem,1.4vw,1.125rem)] break-words leading-tight">
            {stats.threats_blocked?.toLocaleString() || "0"}
          </p>
          <p className="text-[9px] text-slate-600 leading-tight">Policy-approved security responses applied</p>
      </div>

      {/* Metric 3: REAL RUNTIME */}
      <div className="space-y-1">
          <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Runtime</p>
          <p className="text-teal-400 font-mono text-[clamp(0.95rem,1.4vw,1.125rem)] break-words leading-tight">{stats.uptime_string || "0h 0m"}</p>
          <p className="text-[9px] text-slate-600 leading-tight">Live deployment with zero service interruption</p>
      </div>

      {/* Metric 4: REAL AI CONFIDENCE */}
      <div className="space-y-1">
          <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">Pattern Rate </p>
          <div className="flex items-center gap-2">
            <p className="text-purple-400 font-mono text-[clamp(0.95rem,1.4vw,1.125rem)] break-words leading-tight">{stats.ai_confidence}%</p>
            <div className="h-1.5 w-12 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 transition-all duration-1000" 
                  style={{ width: `${stats.ai_confidence}%` }}
                ></div>
            </div>
          </div>
          <p className="text-[9px] text-slate-600 leading-tight">Behavioral patterns learned and refined.</p>
      </div>

      </div>
      <p className="p-1 rounded-1xl bg-gradient-to-b from-slate-900 to-slate-1000 shadow-5xl"></p>
      {/* The Flowing Logs */}
      <div className="flex-1 relative p-6 space-y-4">
         {/* Vertical Matrix Line Effect */}
         {/*<div className="absolute top-0 left-8 w-px h-full bg-slate-800 z-0"></div>*/}

         {displayLogs.map((log, i) => {
            const Icon = ICONS[log.module] || Terminal;
            // Opacity fade for older items
            const opacity = Math.max(0.2, 1 - (i * 0.1)); 
            
            return (
                <div 
                    key={`${log.created_at}-${i}`} 
                    style={{ opacity }}
                    className="flex items-center gap-4 relative z-10 transition-all duration-500 transform translate-y-0 w-full"
                >
                    <span className="text-slate-500 w-16 text-right flex-shrink-0">{log.created_at}</span>
                    
                    <div className="relative flex-shrink-0">
                        <div className="w-2 h-2 bg-slate-800 rounded-full border border-slate-600"></div>
                    </div>

                    <div className="flex items-center gap-3 bg-slate-900/50 px-3 py-1.5 rounded border border-slate-800/50 flex-1 min-w-0">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${log.status === 'BLOCKED' ? 'text-red-500' : 'text-teal-400'}`} />
                        <span className="text-emerald-400 font-bold whitespace-nowrap">[{log.module}]</span>
                        
                        <span className="text-slate-300 truncate block">{log.event}</span>
                        
                        <span className={`ml-auto font-bold flex-shrink-0 ${log.status === 'BLOCKED' ? 'text-red-500' : 'text-emerald-600'}`}>
                            {log.status}
                        </span>
                    </div>
                </div>
                );
            })}
      </div>
      
      
      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-[#020617] to-transparent pointer-events-none z-20"></div>
      
    </div>
    
  );
};

export default MatrixFeed;